import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import * as XLSX from "xlsx";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { trpc } from "../../lib/trpc";
import "./EmployeeBulkImportDrawer.css";

/**
 * STEPERP — İK / Personel TOPLU EXCEL İÇE AKTARMA.
 *
 * Akış: Şablonu indir (Referans sayfası pozisyon/birim/yönetici adlarını taşır) → doldur → yükle.
 * Dosya İSTEMCİDE ayrıştırılır (SheetJS), sunucuya yalnız JSON satırlar gider.
 * Sunucu davranışı: mevcut personel ATLANIR + raporlanır; hatalı satır raporlanır ama
 * GEÇERLİ satırlar yine de oluşturulur. Rapor ekranı satır numarası + gerekçe gösterir.
 *
 * Tasarım sistemi KATI: yerel dosya girdisi gizli + `.btn-md secondary` tetikleyici, beklenen
 * sütunlar `.fgroup` içinde `.badge-pill` rozetleri, inline stil yok. (Eski `BulkStockImportDrawer`
 * hâlâ ham `<input type=file>` + `<code>` dökümü kullanıyor — ORASI referans DEĞİLDİR.)
 */

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

/** Şablon kolonları (sıra önemli). Zorunlu: Ad, Soyad — kalanlar opsiyonel. */
const COLUMNS = [
  "Ad", "Soyad", "TC Kimlik No", "Telefon", "E-posta",
  "Doğum Tarihi", "Cinsiyet", "Medeni Durum",
  "Pozisyon", "Birim", "Yönetici",
  "Çalışma Şekli", "Durum", "İşe Giriş Tarihi", "Çıkış Tarihi",
  "Adres", "Acil Durum Kişisi", "Acil Durum Telefonu", "IBAN", "Personel Kodu",
];
const COL_WIDTHS = [16, 16, 14, 14, 24, 13, 11, 13, 22, 22, 22, 14, 10, 14, 13, 30, 20, 16, 28, 16];
/** Sunucunun zorunlu tuttuğu sütunlar — rozet listesinde primary ton + `*` ile işaretlenir. */
const REQUIRED_COLS = new Set(["Ad", "Soyad"]);

/** Önizlemede gösterilen kolonlar (dar drawer'da okunur kalsın). */
const PREVIEW_COLS = ["Ad", "Soyad", "TC Kimlik No", "Pozisyon", "Birim", "İşe Giriş Tarihi"];

/** Excel'de serbest metin yerine seçilebilir etiketler — Referans sayfasına yazılır. */
const GENDER_OPTS = ["Erkek", "Kadın", "Diğer", "Belirtilmemiş"];
const MARITAL_OPTS = ["Bekâr", "Evli", "Boşanmış", "Dul", "Belirtilmemiş"];
const EMP_TYPE_OPTS = ["Tam zamanlı", "Yarı zamanlı", "Sözleşmeli", "Stajyer", "Geçici"];
const STATUS_OPTS = ["Aktif", "İzinli", "Ayrıldı", "Aday"];

/**
 * Örnek satır — "Personeller" sayfasına YAZILMAZ, yalnız Referans sayfasında gösterilir.
 * Veri sayfasına örnek koyulsaydı silmeyi unutan kullanıcı sahte "Ahmet Yılmaz" personelini
 * gerçekmiş gibi içe aktarırdı (11 haneli örnek TC doğrulamadan geçer).
 */
const SAMPLE_LABEL = "ÖRNEK (bu satırı kopyalamayın, biçim örneğidir)";
const SAMPLE: Record<string, string> = {
  "Ad": "Ahmet", "Soyad": "Yılmaz", "TC Kimlik No": "12345678901",
  "Telefon": "0555 123 45 67", "E-posta": "ahmet.yilmaz@ornek.com",
  "Doğum Tarihi": "15.06.1990", "Cinsiyet": "Erkek", "Medeni Durum": "Evli",
  "Çalışma Şekli": "Tam zamanlı", "Durum": "Aktif", "İşe Giriş Tarihi": "01.03.2020",
};

/** Excel seri numarası → GG.AA.YYYY (önizlemede ham 43891 yerine tarih görünsün). */
function serialToTr(v: unknown): string {
  if (typeof v !== "number" || !Number.isFinite(v) || v <= 0 || v >= 100000) return String(v);
  const d = new Date(Date.UTC(1899, 11, 30) + v * 86_400_000);
  if (Number.isNaN(d.getTime())) return String(v);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getUTCDate())}.${p(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}`;
}
const DATE_COLS = new Set(["Doğum Tarihi", "İşe Giriş Tarihi", "Çıkış Tarihi"]);

/** TR sayı biçimi (1.234) — satır sayıları binliği ayrılmış gösterilir. */
const fmtInt = (n: number) => n.toLocaleString("tr-TR");

/** Seçili dosya satırındaki özet: "okunamadı" ile "okundu ama kural dışı" ayrı şeylerdir. */
function fileMetaText(m: { kind: "ok" | "empty" | "tooMany" | "invalid"; count: number }): string {
  if (m.kind === "ok") return `${fmtInt(m.count)} satır`;
  if (m.kind === "empty") return "Boş";
  if (m.kind === "tooMany") return `${fmtInt(m.count)} satır · limit aşıldı`;
  return "Okunamadı";
}

/** Sunucunun kabul ettiği Ad/Soyad başlık adları (alias listesi ikisinde de aynı olmalı). */
const NAME_ALIASES = { first: ["Ad", "Adı", "İsim", "firstName", "First Name"], last: ["Soyad", "Soyadı", "lastName", "Last Name"] };
const pickName = (r: Record<string, unknown>, keys: string[]): string =>
  keys.map((k) => String(r[k] ?? "").trim()).find((v) => v !== "") ?? "";

export function EmployeeBulkImportDrawer({ open, onClose, onImported }: Props) {
  const [fileName, setFileName] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<Array<Record<string, unknown>>>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  /** Dosyanın okunma SONUCU — satır özetindeki metni bu belirler (parseError'ın varlığı değil):
   *  "okunamadı" ile "okundu ama kural dışı" farklı şeylerdir ve kullanıcıya farklı iş yaptırır. */
  const [fileMeta, setFileMeta] = useState<{ kind: "ok" | "empty" | "tooMany" | "invalid"; count: number } | null>(null);
  /** Şablon indirme hatası AYRI tutulur; dosya hatasıyla aynı state'e yazılırsa biri diğerini siler. */
  const [tplError, setTplError] = useState<string | null>(null);
  const [building, setBuilding] = useState(false);
  const [report, setReport] = useState<Awaited<ReturnType<typeof importMut.mutateAsync>> | null>(null);

  const refsQ = trpc.hr.employee.bulkTemplateRefs.useQuery(undefined, { enabled: false });
  const importMut = trpc.hr.employee.bulkImport.useMutation();

  const reset = useCallback(() => {
    setFileName("");
    setRows([]);
    setParseError(null);
    setFileMeta(null);
    setTplError(null);
    setReport(null);
    // Aynı dosya yeniden seçilebilsin: input değeri değişmezse change tetiklenmez.
    if (fileRef.current) fileRef.current.value = "";
    importMut.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function handleClose() {
    reset();
    onClose();
  }

  /** Şablon: "Personeller" (başlık + örnek satır) + "Referans" (geçerli ad listeleri). */
  async function downloadTemplate() {
    setBuilding(true);
    setTplError(null);
    try {
      const res = await refsQ.refetch();
      if (res.error) { setTplError(`Şablon verisi alınamadı: ${res.error.message}`); return; }
      const refs = res.data ?? { positions: [], orgUnits: [], managers: [] };

      const wb = XLSX.utils.book_new();

      // Veri sayfası YALNIZ başlık satırı içerir (örnek satır Referans'ta) — kullanıcı 2. satırdan yazar.
      const ws = XLSX.utils.aoa_to_sheet([COLUMNS]);
      ws["!cols"] = COL_WIDTHS.map((wch) => ({ wch }));
      XLSX.utils.book_append_sheet(wb, ws, "Personeller");

      // Referans sayfası: kolonlar bağımsız uzunlukta → satır satır dizi kur.
      // Yönetici SADE AD yazılır; kod ayrı sütunda (birleşik "Ad (KOD)" hücresi kopyalandığında
      // sunucudaki ad/kod sözlüğüne uymaz ve "Yönetici bulunamadı" hatası verirdi).
      const maxLen = Math.max(
        refs.positions.length, refs.orgUnits.length, refs.managers.length,
        GENDER_OPTS.length, MARITAL_OPTS.length, EMP_TYPE_OPTS.length, STATUS_OPTS.length,
      );
      const refHeader = ["Pozisyon", "Birim", "Yönetici (ad)", "Yönetici Kodu", "Cinsiyet", "Medeni Durum", "Çalışma Şekli", "Durum"];
      const refRows: string[][] = [refHeader];
      for (let i = 0; i < maxLen; i++) {
        refRows.push([
          refs.positions[i]?.label ?? "",
          refs.orgUnits[i]?.label ?? "",
          refs.managers[i]?.label ?? "",
          refs.managers[i]?.code ?? "",
          GENDER_OPTS[i] ?? "",
          MARITAL_OPTS[i] ?? "",
          EMP_TYPE_OPTS[i] ?? "",
          STATUS_OPTS[i] ?? "",
        ]);
      }
      // Biçim örneği en altta, açıkça etiketli (veri sayfasına kaçamaz).
      refRows.push([], [SAMPLE_LABEL], COLUMNS, COLUMNS.map((h) => SAMPLE[h] ?? ""));
      const wsRef = XLSX.utils.aoa_to_sheet(refRows);
      wsRef["!cols"] = [{ wch: 26 }, { wch: 26 }, { wch: 26 }, { wch: 18 }, { wch: 15 }, { wch: 15 }, { wch: 16 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(wb, wsRef, "Referans");

      XLSX.writeFile(wb, "STEPERP_personel_sablonu.xlsx");
    } finally {
      setBuilding(false);
    }
  }

  async function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    setParseError(null);
    setFileMeta(null);
    setReport(null);
    setRows([]);
    /** Okuma sonucunu hem mesaja hem satır özetine tek yerden yazar. */
    const fail = (kind: "empty" | "tooMany" | "invalid", msg: string, count = 0) => {
      setParseError(msg);
      setFileMeta({ kind, count });
    };
    try {
      const ab = await f.arrayBuffer();
      // raw:true KRİTİK — aksi halde SheetJS tarih hücresini kullanıcının Excel yerel biçimine
      // çevirir ("15.06.1990" → "10/10/95" gibi 2 haneli/ters sıralı metin) ve gün/ay belirsizleşir.
      // Ham modda .xlsx tarihi Excel SERİ NUMARASI (kesin), .csv hücresi ise metin olarak gelir;
      // sunucudaki parseDate ikisini de doğru çözer.
      const wb = XLSX.read(ab, { raw: true });
      // "Personeller" sayfası varsa onu al (Referans sayfası yanlışlıkla okunmasın).
      const sheetName = wb.SheetNames.find((n) => n === "Personeller") ?? wb.SheetNames[0];
      if (!sheetName) { fail("invalid", "Dosyada sayfa bulunamadı."); return; }
      const sheet = wb.Sheets[sheetName]!;
      const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: null, raw: true });
      if (raw.length === 0) { fail("empty", "Dosya boş veya başlık satırından sonra veri yok."); return; }
      if (raw.length > 2000) { fail("tooMany", `Tek seferde en fazla 2000 satır aktarılabilir (dosyada ${fmtInt(raw.length)} satır var).`, raw.length); return; }
      // SheetJS boş satırları ATLAR → dizi indeksi dosyadaki satırla kayar. Gerçek satır numarası
      // `__rowNum__` içinde ama SAYILAMAZ (non-enumerable) → JSON'a girmesi için gerçek alana kopyala.
      // Başlıklardaki görünmez boşluklar da burada kırpılır (Excel'de fark edilmez, eşleşmeyi bozar).
      const parsed = raw.map((r, i) => {
        const t: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(r)) t[k.trim()] = v;
        const excelRow = (r as { __rowNum__?: number }).__rowNum__;
        t["__row"] = typeof excelRow === "number" ? excelRow + 1 : i + 2;
        return t;
      });
      const hasName = parsed.some((r) => pickName(r, NAME_ALIASES.first) || pickName(r, NAME_ALIASES.last));
      if (!hasName) { fail("invalid", '"Ad" / "Soyad" sütunları bulunamadı — şablonun başlık satırını değiştirmeyin.'); return; }
      setRows(parsed);
      setFileMeta({ kind: "ok", count: parsed.length });
    } catch (err) {
      fail("invalid", err instanceof Error ? err.message : String(err));
    } finally {
      // Aynı dosya düzeltilip yeniden seçilebilsin (değer değişmezse change tetiklenmez).
      e.target.value = "";
    }
  }

  async function handleSubmit() {
    if (rows.length === 0) return;
    try {
      // Satırlar FİLTRESİZ gider: adı boş bir satırı istemcide elemek sessiz veri kaybı olurdu —
      // sunucu onu "Ad ve Soyad zorunlu" diye raporlasın ki kullanıcı hangi satırın düştüğünü görsün.
      const result = await importMut.mutateAsync({
        rows: rows as Array<Record<string, string | number | boolean | null>>,
      });
      setReport(result);
    } catch {
      // Mesaj `importMut.error` şeridinden gösterilir; burada yutulmazsa yakalanmamış promise hatası olur.
    } finally {
      // Satır-başına transaction: hata ortasında bile bir kısmı YAZILMIŞ olabilir → liste her hâlükârda tazelenir.
      onImported();
    }
  }

  const preview = useMemo(() => rows.slice(0, 6), [rows]);

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title="Personel Toplu İçe Aktar"
      subtitle={rows.length > 0 ? `${fmtInt(rows.length)} satır hazır` : "Şablon indir → doldur → yükle"}
      size="lg"
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={handleClose} disabled={importMut.isPending}>
            {report ? "Kapat" : "İptal"}
          </button>
          {!report ? (
            <button type="button" className="btn-md primary" onClick={handleSubmit} disabled={rows.length === 0 || importMut.isPending}>
              {importMut.isPending ? "Aktarılıyor…" : rows.length > 0 ? `${fmtInt(rows.length)} Satırı İçe Aktar` : "İçe Aktar"}
            </button>
          ) : null}
        </>
      }
    >
      {!report ? (
        <>
          {/* .form-banner-info bir FLEX kabı: her metin parçası ayrı öğe olup tek satıra dizilir →
              çok cümleli metin TEK sarmalayıcıya alınır (aksi halde yatay taşma olur). */}
          <div className="form-banner-info">
            <Icon name="info" size={14} className="ebi-bnr-ic" />
            <div>
              Zaten kayıtlı personeller (aynı TC, aynı personel kodu ya da TC'siz aynı ad-soyad) <b>atlanır</b> ve raporda
              listelenir; hatalı satırlar raporlanır, geçerli satırlar yine de aktarılır.
            </div>
          </div>

          {/* 1 — Şablon. Buton satır kabına alınır: `.form-field` column-flex + align-items:stretch
              olduğundan doğrudan çocuk olan .btn-md drawer genişliğine yayılıyordu. */}
          <div className="form-field">
            <span className="lbl">Şablon</span>
            <div className="ebi-pick">
              <button type="button" className="btn-md secondary" onClick={downloadTemplate} disabled={building}>
                <Icon name="download" size={14} />{building ? "Hazırlanıyor…" : "Şablonu İndir"}
              </button>
            </div>
            {tplError ? <span className="err" role="alert">{tplError}</span> : null}
            <span className="help">
              İki sayfalı .xlsx: <b>Personeller</b> (doldurulacak) + <b>Referans</b> — <b>Pozisyon</b>, <b>Birim</b> ve{" "}
              <b>Yönetici</b> sütunlarına Referans sayfasındaki adları yazın.
            </span>
          </div>

          {/* 2 — Beklenen sütunlar: `.fgroup` kabuğu + `.badge-pill` rozetleri (ham mono döküm yerine). */}
          <div className="fgroup ebi-cols-group">
            <div className="fgroup-head">
              <span className="ic"><Icon name="columns" size={13} /></span>
              <span className="t">Beklenen sütunlar</span>
              <span className="s mono">{COLUMNS.length} sütun</span>
            </div>
            <div className="fgroup-body">
              <div className="ebi-cols">
                {COLUMNS.map((c) => {
                  const req = REQUIRED_COLS.has(c);
                  return (
                    <span key={c} className={`badge-pill${req ? " b-primary" : ""}`} title={req ? "Zorunlu sütun" : "Opsiyonel sütun"}>
                      {c}
                      {req ? (<><span className="rq" aria-hidden="true">*</span><span className="sr-only"> (zorunlu)</span></>) : null}
                    </span>
                  );
                })}
              </div>
              <p className="ebi-cols-legend">
                <b>*</b> zorunlu — kalan sütunlar boş bırakılabilir. Başlık satırını değiştirmeyin;
                tarihler <span className="mono">GG.AA.YYYY</span> biçiminde olmalı.
              </p>
            </div>
          </div>

          {/* 3 — Dosya. Yerel input gizli; tetikleyici gerçek bir <button> → klavye ile odaklanır,
              .btn-md:focus-visible halkası görünür (OrderAttachmentPanel'deki baskın desen). */}
          <div className="form-field">
            {/* <label htmlFor> gizli input'a da tıklamayı iletir → görünen metin tıklanabilir kalır. */}
            <label className="lbl" htmlFor="ebi-file">Doldurulmuş dosya</label>
            <input
              ref={fileRef} id="ebi-file" type="file" accept=".xlsx,.xls,.csv"
              className="ebi-file-native" onChange={handleFile} disabled={importMut.isPending}
            />
            <div className="ebi-pick">
              <button
                type="button" className="btn-md secondary"
                onClick={() => fileRef.current?.click()} disabled={importMut.isPending}
                aria-describedby="ebi-file-help"
              >
                <Icon name="upload" size={14} />{fileName ? "Dosyayı Değiştir" : "Dosya Seç"}
              </button>
            </div>
            {!fileName ? (
              <div className="ebi-empty">Henüz dosya seçilmedi — doldurduğunuz .xlsx, .xls veya .csv dosyasını seçin.</div>
            ) : (
              <div className={`ebi-file-row${fileMeta && fileMeta.kind !== "ok" ? " err" : ""}${fileMeta?.kind === "ok" ? " ok" : ""}`}>
                <span className="ic"><Icon name={fileMeta && fileMeta.kind !== "ok" ? "alertTriangle" : "sheet"} size={14} /></span>
                <span className="nm" title={fileName}>{fileName}</span>
                <span className="mt" aria-live="polite">{fileMeta ? fileMetaText(fileMeta) : "Okunuyor…"}</span>
                <button
                  type="button" className="input-icon-btn" aria-label="Seçilen dosyayı kaldır" title="Kaldır"
                  onClick={reset} disabled={importMut.isPending}
                >
                  <Icon name="x" size={14} />
                </button>
              </div>
            )}
            <span className="help" id="ebi-file-help">Tek seferde en fazla 2000 satır aktarılabilir.</span>
          </div>

          {parseError ? <div className="form-banner-err" role="alert">{parseError}</div> : null}
          {/* Aktarım hatası önizleme TABLOSUNUN ÜSTÜNDE: altında kalsaydı kaydırma gerektirir,
              kullanıcı butona basar ve hiçbir şey olmamış gibi görünürdü. */}
          {importMut.error ? <div className="form-banner-err" role="alert">{importMut.error.message}</div> : null}

          {rows.length > 0 ? (
            <div className="ebi-sec">
              <div className="ebi-sec-ttl">Önizleme (ilk {preview.length} / toplam {fmtInt(rows.length)} satır)</div>
              <div className="ebi-box">
                <table className="ebi-tbl">
                  <thead>
                    <tr>
                      <th className="w-row">Satır</th>
                      {PREVIEW_COLS.map((c) => <th key={c}>{c}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((r, i) => (
                      <tr key={i}>
                        <td className="num">{String(r["__row"] ?? i + 2)}</td>
                        {PREVIEW_COLS.map((c) => {
                          const v = r[c];
                          const blank = v == null || String(v).trim() === "";
                          // Tarih hücreleri ham Excel seri numarası olarak gelir (43891) → okunur biçim.
                          const txt = DATE_COLS.has(c) ? serialToTr(v) : String(v);
                          return <td key={c} className={blank ? "empty" : undefined}>{blank ? "—" : txt}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

        </>
      ) : (
        <>
          <div className={`ebi-result${report.errors.length > 0 ? " warn" : ""}`}>
            <div className="ttl">
              <Icon name={report.errors.length > 0 ? "alertTriangle" : "check"} size={14} />
              {report.errors.length > 0 ? "İçe aktarma tamamlandı — bazı satırlar alınmadı" : "İçe aktarma tamamlandı"}
            </div>
            <div className="nums">
              Toplam {fmtInt(report.total)} · Aktarılan {fmtInt(report.created)} · Atlanan {fmtInt(report.skipped)}
            </div>
          </div>

          {report.errors.length > 0 ? (
            <div>
              <div className="ebi-sec-ttl">Alınmayan satırlar ({report.errors.length})</div>
              <div className="ebi-box tall">
                <table className="ebi-tbl">
                  <thead>
                    <tr>
                      <th className="w-row">Satır</th>
                      <th className="w-name">Personel</th>
                      <th>Gerekçe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.errors.map((e, i) => (
                      <tr key={i}>
                        <td className="num">{e.row}</td>
                        <td>{e.name ?? "—"}</td>
                        <td className="msg">{e.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="ebi-note">
                Satır numaraları Excel dosyasındaki satırla aynıdır. Düzeltip yalnız bu satırları içeren
                bir dosyayı tekrar yükleyebilirsiniz — aktarılmış personeller yeniden oluşturulmaz.
              </p>
            </div>
          ) : null}
        </>
      )}
    </Drawer>
  );
}
